import { useQuery, useQueries, UseQueryResult, keepPreviousData } from '@tanstack/react-query';
import { useAppContext } from '../../contexts/Storage';
import { apiClient } from '../../utils/api';
import { UrlBuilder } from '../../utils/urls';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubscriptionItem {
  user_id: number;
  subscription_id: number;
  created_at: string;
  updated_at: string;
  transaction_reference: string;
  subscription_status: string;
  subscription_name: string;
  subscription_type: string;
  subscription_duration: number;
  subscription_price: number;
}

export interface SubscriptionPlan {
  id: number;
  subscription_name: string;
  subscription_type: string;
  subscription_duration: number;
  subscription_price: number;
}

export interface SubscriptionFeature {
  id: number;
  feature_name: string;
  feature_code: string;
  feature_description: string;
  feature_credit_point_price: number;
}

export interface FindSubscriptionsFilter {
  id?: number;
  subscription_type?: string;
  min_price?: string;
  max_price?: string;
}

export interface FindSubscriptionsApiResponse {
  current_page: number;
  data: SubscriptionPlan[];
  first_page_url: string;
  from: number | null;
  last_page: number;
  last_page_url: string;
  links: Array<{ url: string | null; label: string; active: boolean }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
}

export interface UserSubscriptionFilter {
  subscription_id?: number;
  subscription_status?: string;
  start_date?: string;
  end_date?: string;
}

export interface UserSubscriptionPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface UserSubscriptionApiResponse {
  current_page: number;
  data: SubscriptionItem[];
  first_page_url: string;
  from: number | null;
  last_page: number;
  last_page_url: string;
  links: Array<{ url: string | null; label: string; active: boolean }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
}

export interface UserSubscriptionResponse {
  subscriptions: SubscriptionItem[];
  pagination: UserSubscriptionPagination;
  activeSubscription: SubscriptionItem | null;
}

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const USER_SUBSCRIPTION_QUERY_KEY = ['user-subscription'] as const;
export const ALL_SUBSCRIPTIONS_QUERY_KEY = ['all-subscriptions'] as const;
export const SUBSCRIPTION_FEATURES_QUERY_KEY = ['subscription-features'] as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export interface UseGetUserSubscriptionParams {
  userId?: number;
  filters?: UserSubscriptionFilter;
  enabled?: boolean;
}

/**
 * Fetches user subscription data.
 * Requires user_id in the URL path.
 */
export function useGetUserSubscription({
  userId,
  filters,
  enabled = true,
}: UseGetUserSubscriptionParams = {}): UseQueryResult<UserSubscriptionResponse, Error> {
  const { token, user } = useAppContext();

  const effectiveUserId = userId ?? user?.id;

  return useQuery({
    queryKey: [...USER_SUBSCRIPTION_QUERY_KEY, effectiveUserId, filters],
    queryFn: async () => {
      if (!token || !effectiveUserId) {
        throw new Error('User not authenticated');
      }

      const requestBody: UserSubscriptionFilter = {
        subscription_status: filters?.subscription_status || 'active',
        ...(filters?.subscription_id && { subscription_id: filters.subscription_id }),
        ...(filters?.start_date && { start_date: filters.start_date }),
        ...(filters?.end_date && { end_date: filters.end_date }),
      };

      const response = await apiClient.postWithToken<UserSubscriptionApiResponse>(
        UrlBuilder.subscriptions.userSubscription(effectiveUserId),
        token,
        requestBody
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch user subscription');
      }

      const data = response.data as UserSubscriptionApiResponse;

      const activeSubscription = data.data.find(
        (sub) => sub.subscription_status === 'active'
      ) || null;

      return {
        subscriptions: data.data,
        pagination: {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
        },
        activeSubscription,
      };
    },
    enabled: enabled && !!token && !!effectiveUserId,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Find All Subscription Plans
// ---------------------------------------------------------------------------

export interface UseGetAllSubscriptionsParams {
  filters?: FindSubscriptionsFilter;
  enabled?: boolean;
}

/**
 * Fetches all available subscription plans.
 * POST /api/subscriptions/find
 */
export function useGetAllSubscriptions({
  filters,
  enabled = true,
}: UseGetAllSubscriptionsParams = {}): UseQueryResult<SubscriptionPlan[], Error> {
  const { token } = useAppContext();

  return useQuery({
    queryKey: [...ALL_SUBSCRIPTIONS_QUERY_KEY, filters],
    queryFn: async () => {
      if (!token) {
        throw new Error('User not authenticated');
      }

      const requestBody: Record<string, unknown> = {};
      if (filters?.id !== undefined) requestBody.id = filters.id;
      if (filters?.subscription_type) requestBody.subscription_type = filters.subscription_type;
      if (filters?.min_price) requestBody.min_price = filters.min_price;
      if (filters?.max_price) requestBody.max_price = filters.max_price;

      const response = await apiClient.postWithToken<FindSubscriptionsApiResponse>(
        UrlBuilder.subscriptions.find(),
        token,
        requestBody
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch subscription plans');
      }

      return (response.data as FindSubscriptionsApiResponse).data;
    },
    enabled: enabled && !!token,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Get Subscription Features
// ---------------------------------------------------------------------------

export interface UseGetSubscriptionFeaturesParams {
  subscriptionId: number;
  enabled?: boolean;
}

/**
 * Fetches features for a specific subscription plan.
 * POST /api/subscriptions/list/features
 */
export function useGetSubscriptionFeatures({
  subscriptionId,
  enabled = true,
}: UseGetSubscriptionFeaturesParams): UseQueryResult<SubscriptionFeature[], Error> {
  const { token } = useAppContext();

  return useQuery({
    queryKey: [...SUBSCRIPTION_FEATURES_QUERY_KEY, subscriptionId],
    queryFn: async () => {
      if (!token) {
        throw new Error('User not authenticated');
      }

      const response = await apiClient.postWithToken<SubscriptionFeature[]>(
        UrlBuilder.subscriptions.features(),
        token,
        { subscription_id: subscriptionId }
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch subscription features');
      }

      return response.data as SubscriptionFeature[];
    },
    enabled: enabled && !!token && !!subscriptionId,
  });
}

/**
 * Fetches features for multiple subscription plans in parallel.
 */
export function useGetMultipleSubscriptionFeatures(
  subscriptionIds: number[],
  enabled = true
) {
  const { token } = useAppContext();

  return useQueries({
    queries: subscriptionIds.map((id) => ({
      queryKey: [...SUBSCRIPTION_FEATURES_QUERY_KEY, id],
      queryFn: async () => {
        if (!token) throw new Error('User not authenticated');

        const response = await apiClient.postWithToken<SubscriptionFeature[]>(
          UrlBuilder.subscriptions.features(),
          token,
          { subscription_id: id }
        );

        if (!response.success) {
          throw new Error(response.error || 'Failed to fetch subscription features');
        }

        return { subscriptionId: id, features: response.data as SubscriptionFeature[] };
      },
      enabled: enabled && !!token && !!id,
    })),
  });
}
